import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import axiosAdmin from '../../services/axiosAdmin';
import { displayErrors, formataMoeda } from '../../util/diversos';

import '../empresa/estamparia/dashboard/dashboardEstamparia.css';
import './dashboardEmpresa.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

/*
  Dashboard da tela inicial do ADMINISTRADOR (Elo):
  empresas e licenças, mensalidades (tblelorecebimento), tickets e acessos.
*/

const PERIODOS = [
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'mes', label: 'Este mês' },
  { id: 'mesAnterior', label: 'Mês anterior' },
  { id: 'ano', label: 'Este ano' },
];

const SITUACAO_TICKET = {
  ABERTO: { label: 'Abertos', icon: 'pi-inbox', classe: 'is-aberto' },
  ANDAMENTO: { label: 'Em andamento', icon: 'pi-clock', classe: 'is-pendente' },
  FECHADO: { label: 'Fechados', icon: 'pi-check-circle', classe: 'is-fechado' },
  IMPLEMENTADO: { label: 'Implementados', icon: 'pi-check-square', classe: 'is-fechado' },
};

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function calcularPeriodo(id) {
  const hoje = moment();
  switch (id) {
    case '7d': return [hoje.clone().subtract(6, 'days'), hoje];
    case '30d': return [hoje.clone().subtract(29, 'days'), hoje];
    case 'mesAnterior': {
      const inicio = hoje.clone().subtract(1, 'month').startOf('month');
      return [inicio, inicio.clone().endOf('month')];
    }
    case 'ano': return [hoje.clone().startOf('year'), hoje];
    default: return [hoje.clone().startOf('month'), hoje];
  }
}

function paraMoment(data) {
  if (!data) return null;
  if (Array.isArray(data)) return moment([data[0], data[1] - 1, data[2]]);
  return moment(String(data).substring(0, 10), 'YYYY-MM-DD');
}

function moeda(valor) {
  return formataMoeda(Number(valor) || 0);
}

function moedaCurta(valor) {
  const v = Number(valor) || 0;
  if (Math.abs(v) >= 1000000) return `R$ ${(v / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (Math.abs(v) >= 10000) return `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return moeda(v);
}

function numero(valor) {
  return (Number(valor) || 0).toLocaleString('pt-BR');
}

function variacao(atual, anterior) {
  const a = Number(atual) || 0;
  const b = Number(anterior) || 0;
  if (b <= 0) return null;
  const perc = ((a / b) - 1) * 100;
  return `${perc >= 0 ? '+' : ''}${perc.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs. período anterior`;
}

function lerCor(variavel, padrao) {
  try {
    const cor = getComputedStyle(document.documentElement).getPropertyValue(variavel).trim();
    return cor || padrao;
  } catch (e) {
    return padrao;
  }
}

// eslint-disable-next-line react/prop-types
function Cartao({ titulo, icone, link, onLink, children, classe }) {
  return (
    <section className={`dash-card ${classe || ''}`}>
      <header className="dash-card-header">
        <h2><i className={`pi ${icone}`} />{titulo}</h2>
        {link ? <button type="button" className="dash-link" onClick={onLink}>{link}</button> : null}
      </header>
      {children}
    </section>
  );
}

// eslint-disable-next-line react/prop-types
function DataLista({ data, atraso }) {
  const m = paraMoment(data);
  if (!m) return <span className="dash-date">--</span>;
  const dias = moment().startOf('day').diff(m.clone().startOf('day'), 'days');
  const vencido = atraso === undefined ? dias > 0 : atraso;
  return (
    <span className={`dash-date ${vencido ? 'is-late' : ''}`}>
      {m.format('DD/MM/YY')}
      {dias > 0 ? <em>{dias} {dias === 1 ? 'dia' : 'dias'}</em> : null}
      {dias === 0 ? <em>hoje</em> : null}
      {dias < 0 ? <em>{DIAS_SEMANA[m.day()]}</em> : null}
    </span>
  );
}

export default function DashboardAdmin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user) || {};

  const [periodo, setPeriodo] = useState('mes');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [tema, setTema] = useState(document.documentElement.getAttribute('data-theme'));

  useEffect(() => {
    const observer = new MutationObserver(() => setTema(document.documentElement.getAttribute('data-theme')));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      try {
        setCarregando(true);
        setErro('');
        const [inicio, fim] = calcularPeriodo(periodo);
        const params = `/admindashboard?dataInicial=${inicio.format('DD-MM-YYYY')}&dataFinal=${fim.format('DD-MM-YYYY')}`;
        const { data } = await axiosAdmin.get(params);
        if (ativo) setDados(data);
      } catch (err) {
        if (ativo) {
          const status = get(err, 'response.status', 0);
          const mensagemServidor = get(err, 'response.data.errors[0]', '') || get(err, 'response.data.message', '');
          let detalhe = '';
          if (!err.response) {
            detalhe = 'sem resposta do servidor (backend fora do ar ou endereço incorreto)';
          } else if (status === 404) {
            detalhe = 'serviço /admindashboard não encontrado (backend desatualizado ou versão antiga ainda ativa no Tomcat)';
          } else if (status === 401 || status === 403) {
            detalhe = 'acesso negado';
          } else {
            detalhe = `erro ${status}${mensagemServidor ? `: ${mensagemServidor}` : ''}`;
          }
          setErro(`Não foi possível carregar os indicadores - ${detalhe}.`);
          if (status === 403) displayErrors(err, dispatch, navigate, '', '');
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => { ativo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo]);

  const [inicioPeriodo, fimPeriodo] = calcularPeriodo(periodo);

  const serie = useMemo(() => {
    const lista = get(dados, 'serieDiaria', []) || [];
    const agruparMes = lista.length > 62;
    const mapa = new Map();
    lista.forEach((item) => {
      const dia = paraMoment(item.data);
      if (!dia) return;
      const chave = agruparMes ? dia.format('YYYY-MM') : dia.format('YYYY-MM-DD');
      const rotulo = agruparMes ? `${MESES[dia.month()]}/${dia.format('YY')}` : dia.format('DD/MM');
      const atual = mapa.get(chave) || { rotulo, valor: 0, quantidade: 0 };
      atual.valor += Number(item.valor) || 0;
      atual.quantidade += Number(item.quantidade) || 0;
      mapa.set(chave, atual);
    });
    return { agruparMes, pontos: Array.from(mapa.values()) };
  }, [dados]);

  const grafico = useMemo(() => {
    const cor = lerCor('--brand-600', '#2d7a71');
    const corTexto = '#6b7a86';
    return {
      data: {
        labels: serie.pontos.map(p => p.rotulo),
        datasets: [{
          label: 'Recebido',
          data: serie.pontos.map(p => Math.round(p.valor * 100) / 100),
          backgroundColor: cor,
          hoverBackgroundColor: lerCor('--brand-800', cor),
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: 'bottom',
          maxBarThickness: 28,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1d2a33',
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (itens) => (serie.agruparMes ? itens[0].label : `Dia ${itens[0].label}`),
              label: (item) => {
                const p = serie.pontos[item.dataIndex];
                return [`Recebido: ${moeda(p.valor)}`, `Mensalidades: ${numero(p.quantidade)}`];
              },
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: corTexto, maxRotation: 0, autoSkipPadding: 12, font: { size: 11 } } },
          y: {
            beginAtZero: true,
            grid: { color: '#eef2f4' },
            ticks: { color: corTexto, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => moedaCurta(v) },
          },
        },
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serie, tema]);

  const d = dados || {};
  const ticketsPendentes = (Number(d.ticketsAbertos) || 0) + (Number(d.ticketsAndamento) || 0);
  const totalTicketsPeriodo = (d.ticketsPorSituacao || []).reduce((s, i) => s + (Number(i.quantidade) || 0), 0);
  const modulos = d.ticketsPorModulo || [];
  const maiorModulo = Math.max(1, ...modulos.map(m => Number(m.quantidade) || 0));
  const comparacao = variacao(d.recebidoValor, d.recebidoPeriodoAnteriorValor);

  return (
    <div className="dash-estamparia dash-empresa">
      <div className="dash-top">
        <div>
          <span className="section-eyebrow">Administração</span>
          <h1>Olá{user.nome ? `, ${user.nome.split(' ')[0]}` : ''}!</h1>
          <p>
            {inicioPeriodo.format('DD/MM/YYYY')} a {fimPeriodo.format('DD/MM/YYYY')}
            {carregando ? <span className="dash-loading"><i className="pi pi-spin pi-spinner" /> atualizando</span> : null}
          </p>
        </div>
        <div className="dash-periods" role="group" aria-label="Período">
          {PERIODOS.map(p => (
            <button
              key={p.id}
              type="button"
              className={periodo === p.id ? 'is-active' : ''}
              aria-pressed={periodo === p.id}
              onClick={() => setPeriodo(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {erro && !dados ? (
        <div className="dash-card dash-error"><i className="pi pi-exclamation-triangle" /> {erro}</div>
      ) : null}

      {dados ? (
        <>
          <section className="dash-kpis">
            <button type="button" className="dash-kpi" onClick={() => navigate('/consultaempresa')}>
              <span className="dash-kpi-label"><i className="pi pi-building" />Empresas ativas</span>
              <strong>{numero(d.empresasAtivas)}</strong>
              <span className="dash-kpi-sub">
                de {numero(d.empresasCadastradas)} cadastradas · {numero(d.empresasBloqueadas)} bloqueadas
              </span>
            </button>
            <button type="button" className="dash-kpi" onClick={() => navigate('/consultaempresa')}>
              <span className="dash-kpi-label"><i className="pi pi-sync" />Receita mensal</span>
              <strong>{moedaCurta(d.receitaMensal)}</strong>
              <span className="dash-kpi-sub">{numero(d.empresasMensalistas)} empresas mensalistas ativas</span>
            </button>
            <button type="button" className="dash-kpi" onClick={() => navigate('/relatoriorecebimento')}>
              <span className="dash-kpi-label"><i className="pi pi-dollar" />Recebido</span>
              <strong>{moedaCurta(d.recebidoValor)}</strong>
              <span className="dash-kpi-sub">
                {numero(d.recebidoQuantidade)} mensalidades{comparacao ? ` · ${comparacao}` : ''}
              </span>
            </button>
            <button
              type="button"
              className={`dash-kpi ${Number(d.vencidoValor) > 0 ? 'is-alert' : ''}`}
              onClick={() => navigate('/consultarecebimentoemaberto')}
            >
              <span className="dash-kpi-label"><i className="pi pi-wallet" />A receber</span>
              <strong>{moedaCurta(d.receberAbertoValor)}</strong>
              <span className="dash-kpi-sub">
                {Number(d.vencidoValor) > 0
                  ? `${moedaCurta(d.vencidoValor)} vencidos · ${numero(d.empresasInadimplentes)} empresas inadimplentes`
                  : `${numero(d.receberAbertoQuantidade)} mensalidades em aberto · nenhuma vencida`}
              </span>
            </button>
            <button type="button" className="dash-kpi" onClick={() => navigate('/admconsultaticket')}>
              <span className="dash-kpi-label"><i className="pi pi-ticket" />Tickets pendentes</span>
              <strong>{numero(ticketsPendentes)}</strong>
              <span className="dash-kpi-sub">
                {numero(d.ticketsAbertos)} abertos · {numero(d.ticketsAndamento)} em andamento
              </span>
            </button>
          </section>

          <div className="dash-grid">
            <Cartao titulo={`Recebimentos por ${serie.agruparMes ? 'mês' : 'dia'}`} icone="pi-chart-bar" classe="dash-chart-card">
              <div className="dash-chart">
                {serie.pontos.some(p => p.valor > 0) ? (
                  <Bar data={grafico.data} options={grafico.options} />
                ) : (
                  <p className="dash-empty">Nenhuma mensalidade recebida no período.</p>
                )}
              </div>
            </Cartao>

            <Cartao titulo="Licenças" icone="pi-id-card" link="Ver empresas" onLink={() => navigate('/consultaempresa')}>
              <ul className="dash-fin">
                <li className={Number(d.licencasVencidas) > 0 ? 'is-late' : ''}>
                  <span>Vencidas (empresas ativas)</span>
                  <strong>{numero(d.licencasVencidas)}</strong>
                  <em />
                </li>
                <li>
                  <span>Vencem em 15 dias</span>
                  <strong>{numero(d.licencasVencendo)}</strong>
                  <em />
                </li>
              </ul>
              {(d.licencas || []).length === 0 ? (
                <p className="dash-empty">Nenhuma licença vencida ou a vencer.</p>
              ) : (
                <ul className="dash-list">
                  {d.licencas.map(l => (
                    <li key={l.id}>
                      <div className="dash-list-main">
                        <strong>{l.empresa || 'Empresa sem nome'}</strong>
                        <span>{l.cnpj}</span>
                      </div>
                      <div className="dash-list-side">
                        <DataLista data={l.dataLicenca} />
                        <span className="dash-value">{moeda(l.valorMensal)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>

            <Cartao titulo="Acessos no período" icone="pi-sign-in">
              <ul className="dash-fin">
                <li>
                  <span>Logins no eloadmin</span>
                  <strong>{numero(d.acessosWeb)}</strong>
                  <em />
                </li>
                <li className={Number(d.acessosWebNegados) > 0 ? 'is-late' : ''}>
                  <span>Tentativas negadas</span>
                  <strong>{numero(d.acessosWebNegados)}</strong>
                  <em />
                </li>
                <li>
                  <span>Acessos ao Retaguarda</span>
                  <strong>{numero(d.acessosRetaguarda)}</strong>
                  <em />
                </li>
                <li className="is-total">
                  <span>Empresas usando o Retaguarda</span>
                  <strong>{numero(d.empresasRetaguarda)}</strong>
                  <em />
                </li>
              </ul>
            </Cartao>

            <Cartao
              titulo={`Licenças vencidas${Number(d.licencasVencidas) > 0 ? ` (${numero(d.licencasVencidas)})` : ''}`}
              icone="pi-exclamation-circle"
              link="Ver empresas"
              onLink={() => navigate('/consultaempresa')}
            >
              {(d.licencasVencidasLista || []).length === 0 ? (
                <p className="dash-empty">Nenhuma empresa ativa com licença vencida.</p>
              ) : (
                <ul className="dash-list">
                  {d.licencasVencidasLista.map(l => (
                    <li key={l.id}>
                      <div className="dash-list-main">
                        <strong>{l.empresa || 'Empresa sem nome'}</strong>
                        <span>{l.cnpj}</span>
                      </div>
                      <div className="dash-list-side">
                        <DataLista data={l.dataLicenca} atraso />
                        <span className="dash-value">{moeda(l.valorMensal)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>

            <Cartao titulo="Tickets pendentes" icone="pi-ticket" link="Ver tickets" onLink={() => navigate('/admconsultaticket')}>
              {(d.ticketsPendentes || []).length === 0 ? (
                <p className="dash-empty">Nenhum ticket aberto ou em andamento.</p>
              ) : (
                <ul className="dash-list">
                  {d.ticketsPendentes.map(t => (
                    <li key={t.id}>
                      <div className="dash-list-main">
                        <strong>{t.empresa || `Ticket ${t.id}`}</strong>
                        <span>{[t.modulo, t.descricao].filter(Boolean).join(' · ') || 'Sem descrição'}</span>
                      </div>
                      <div className="dash-list-side">
                        <DataLista data={t.dataAbertura} atraso={false} />
                        <span className="dash-value">{get(SITUACAO_TICKET, [t.situacao, 'label'], t.situacao)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>

            <Cartao titulo="Tickets do período" icone="pi-list">
              {totalTicketsPeriodo === 0 ? (
                <p className="dash-empty">Nenhum ticket aberto no período.</p>
              ) : (
                <ul className="dash-status">
                  {(d.ticketsPorSituacao || []).map((s) => {
                    const info = SITUACAO_TICKET[s.chave] || { label: s.chave, icon: 'pi-circle', classe: '' };
                    const perc = Math.round((Number(s.quantidade) / totalTicketsPeriodo) * 100);
                    return (
                      <li key={s.chave} className={info.classe}>
                        <i className={`pi ${info.icon}`} />
                        <span>{info.label}</span>
                        <strong>{numero(s.quantidade)}</strong>
                        <em>{perc}%</em>
                      </li>
                    );
                  })}
                </ul>
              )}
              {modulos.length > 0 ? (
                <>
                  <h3 className="dash-subtitulo">Pendentes por módulo</h3>
                  <ul className="dash-bars">
                    {modulos.map(m => (
                      <li key={m.chave} title={`${m.chave}: ${numero(m.quantidade)}`}>
                        <span className="dash-bar-label">{m.chave}</span>
                        <span className="dash-bar-track">
                          <span className="dash-bar-fill" style={{ width: `${(Number(m.quantidade) / maiorModulo) * 100}%` }} />
                        </span>
                        <span className="dash-bar-value">{numero(m.quantidade)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </Cartao>
          </div>
        </>
      ) : null}

      {!dados && carregando ? (
        <div className="dash-kpis is-skeleton">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="dash-kpi" />)}
        </div>
      ) : null}
    </div>
  );
}
